<template>
  <div class="number-step">
    <!-- Complete: number already purchased -->
    <collapsible-section v-if="state === 'complete'" :collapsible="false" title="Number purchased" icon="tim-icons icon-check-2">
      <p class="number-step__lead mb-1">Your business texts from <strong>{{ settings && settings.display_phone_number ? settings.display_phone_number : 'your new number' }}</strong>.</p>
      <p class="number-step__hint mb-0">Your $10 monthly subscription starts when SMS is activated and continues for every billing cycle you keep this number.</p>
    </collapsible-section>

    <!-- Ready: search and pick -->
    <template v-else>
      <collapsible-section title="Find a local number" icon="tim-icons icon-mobile">
        <info-hint icon="icon-square-pin" variant="neutral">
          Search by area code to find a local number your customers will recognize. The number is registered to your business and is yours for as long as SMS stays active.
        </info-hint>
        <label>Area code</label>
        <div class="amount-input-group number-step__search-group">
          <base-input
            class="amount-input-wrapper mb-0"
            placeholder="e.g. 510"
            v-model="areaCode"
            @keydown.enter.native.prevent="search"
          />
          <base-button
            type="primary"
            class="number-step__search-btn"
            :disabled="searching || !areaCodeValid"
            @click="search"
          >
            <i v-if="searching" class="el-icon-loading mr-1" style="color: #ffffff"></i>
            {{ searching ? 'Searching...' : 'Search' }}
          </base-button>
        </div>
        <p v-if="areaCode && !areaCodeValid" class="number-step__hint">Area code must be 3 digits.</p>

        <div v-if="searched && !searching" class="number-step__results">
          <p v-if="!searchResults.length" class="number-step__hint mb-0">
            No numbers are available in {{ lastSearchedAreaCode }} right now, and none were
            found elsewhere in your state. Area codes do run out. Try a neighbouring one,
            or a nearby city's code.
          </p>
          <p v-else-if="isRegionFallback" class="number-step__hint mb-2">
            No numbers left in {{ lastSearchedAreaCode }}. These are available
            {{ fallbackRegion ? 'in ' + fallbackRegion : 'nearby' }} instead.
          </p>
          <div v-if="searchResults.length" class="number-step__grid">
            <div v-for="n in searchResults" :key="n.phone_number" class="number-step__result">
              <div class="number-step__result-info">
                <span class="number-step__result-number">{{ n.friendly_name || n.phone_number }}</span>
                <span class="number-step__result-locality">{{ n.locality || 'US' }}</span>
              </div>
              <base-button type="primary" size="sm" simple :disabled="searching" @click="confirmPurchase(n)">
                Select
              </base-button>
            </div>
          </div>
        </div>
      </collapsible-section>
    </template>
  </div>
</template>

<script>
import swal from 'sweetalert2';
import InfoHint from '@/components/InfoHint.vue';
import CollapsibleSection from '@/pages/Bookings/BookinSiteSettings/components/shared/CollapsibleSection';

export default {
  name: 'SMSOnboardingStepNumber',
  components: {
    InfoHint,
    CollapsibleSection
  },
  props: {
    formData: {
      type: Object,
      required: true
    },
    settings: {
      type: Object,
      default: null
    },
    searchResults: {
      type: Array,
      default: () => []
    },
    searching: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      areaCode: '',
      searched: false,
      // Pinned at search time. The input stays editable, so comparing results
      // against the live field would mislabel them the moment it is retyped.
      lastSearchedAreaCode: ''
    };
  },
  computed: {
    state() {
      const status = this.settings?.registration_status || 'not_started';
      return ['test_pending', 'active', 'suspended'].includes(status) ? 'complete' : 'ready';
    },
    areaCodeValid() {
      return /^\d{3}$/.test(this.areaCode.trim());
    },
    // The backend widens an exhausted area code to the venue's own state rather
    // than returning nothing. Detected here instead of plumbed through three
    // components: if nothing came back in the code we asked for, it widened.
    isRegionFallback() {
      if (!this.searchResults.length || !this.lastSearchedAreaCode) return false;
      return !this.searchResults.some((n) => this.areaCodeOf(n.phone_number) === this.lastSearchedAreaCode);
    },
    fallbackRegion() {
      const first = this.searchResults[0];
      return first && first.region ? first.region : '';
    }
  },
  watch: {
    searchResults() {
      this.searched = true;
    }
  },
  methods: {
    // "+15105551234" -> "510". Returns '' for anything not a US E.164 number.
    areaCodeOf(phoneNumber) {
      const value = String(phoneNumber || '');
      return /^\+1\d{10}$/.test(value) ? value.slice(2, 5) : '';
    },
    search() {
      if (!this.areaCodeValid || this.searching) return;
      this.lastSearchedAreaCode = this.areaCode.trim();
      this.$emit('search-numbers', { area_code: this.lastSearchedAreaCode });
    },
    // ONE confirmation dialog, never chained.
    async confirmPurchase(number) {
      const display = number.friendly_name || number.phone_number;
      const result = await swal({
        title: `Purchase ${display}?`,
        text: 'This number is yours for as long as SMS stays active for this location.',
        type: 'question',
        showCancelButton: true,
        confirmButtonText: 'Purchase number',
        cancelButtonText: 'Cancel'
      });
      if (result.value) {
        this.$emit('purchase-number', { phone_number: number.phone_number });
      }
    }
  }
};
</script>

<style lang="scss" scoped>
// Standard amount-input-group block (per-consumer by convention; reference
// implementation TransactionDrawer.vue). el-input-number parts omitted, the
// inner field here is a plain base-input.
.amount-input-group {
  display: flex;
  align-items: stretch;
  border: 1px solid rgba(225, 78, 202, 0.4);
  border-radius: 0.4285rem;
  overflow: hidden;
  background: rgba(30, 30, 47, 0.6);
  &:focus-within { border-color: #e14eca; }
}
.amount-input-wrapper {
  flex: 1;
  ::v-deep .form-control {
    border: none;
    border-radius: 0;
    background: transparent;
  }
}

.number-step {
  &__lead {
    color: rgba(255, 255, 255, 0.8);
    font-size: 0.875rem;
    line-height: 1.55;
    margin-bottom: 0.75rem;
  }

  &__hint {
    color: rgba(255, 255, 255, 0.65);
    font-size: 0.75rem;
    line-height: 1.5;
    margin-top: 0.25rem;
  }

  // Composite search field on the app's amount-input-group shell (base styles
  // are per-consumer by convention; the light-mode flip is global), with the
  // button as a flush right segment instead of a prefix badge.
  &__search-group {
    max-width: 340px;
  }

  &__search-btn {
    margin: 0;
    border-radius: 0;
    flex-shrink: 0;
  }

  &__results {
    margin-top: 1rem;
  }

  &__grid {
    display: grid;
    // min(100%, 260px): a hard 260px min track overflows the ~252px content
    // column on the smallest phones (audit finding, 2026-07-18)
    grid-template-columns: repeat(auto-fill, minmax(min(100%, 260px), 1fr));
    gap: 10px;
  }

  &__result {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    padding: 10px 14px;
    background: rgba(125, 125, 125, 0.08);
    border: 1px solid rgba(125, 125, 125, 0.15);
    border-radius: 0.375rem;
  }

  &__result-info {
    display: flex;
    flex-direction: column;
    min-width: 0;
  }

  &__result-number {
    color: rgba(255, 255, 255, 0.9);
    font-size: 0.9375rem;
    font-weight: 600;
    font-variant-numeric: tabular-nums;
  }

  &__result-locality {
    color: rgba(255, 255, 255, 0.55);
    font-size: 0.75rem;
  }
}
</style>

<style>
.white-content .number-step__lead { color: rgba(0, 0, 0, 0.8); }
.white-content .number-step__hint { color: rgba(0, 0, 0, 0.6); }
.white-content .number-step__result-number { color: rgba(0, 0, 0, 0.9); }
.white-content .number-step__result-locality { color: rgba(0, 0, 0, 0.55); }
</style>
